import gsap from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import NextImage from "next/image";
import { useEffect, useRef } from "react";
import CARD_IMG_1 from "@/img/home_easy_1.png";
import CARD_IMG_2 from "@/img/home_easy_2.png";
import CARD_IMG_3 from "@/img/home_easy_3.png";
import CARD_IMG_4 from "@/img/home_easy_4.png";

gsap.registerPlugin(ScrollTrigger);

const steps = [
  {
    img: CARD_IMG_1,
    step: "01",
    title: "Explore Our Menu",
    text: "Browse our weekly rotating menu and choose the meals that fit your lifestyle.",
  },
  {
    img: CARD_IMG_2,
    step: "02",
    title: "We Deliver",
    text: "Convenient Tuesday and Friday delivery straight to your door or nearest pickup location.",
  },
  {
    img: CARD_IMG_3,
    step: "03",
    title: "Heat & Eat",
    text: "Meals are fully prepped and ready to eat in just minutes. No cooking required.",
  },
  {
    img: CARD_IMG_4,
    step: "04",
    title: "Feel Amazing",
    text: "Clean ingredients, incredible flavor. Healthy eating made simple.",
  },
];

export const HowItWorks: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  const stepsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sectionRef.current) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headingRef.current,
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          scrollTrigger: { trigger: headingRef.current, start: "top 85%" },
        },
      );

      const cards = stepsRef.current?.children;
      if (cards) {
        gsap.fromTo(
          cards,
          { y: 60, opacity: 0, scale: 0.95 },
          {
            y: 0,
            opacity: 1,
            scale: 1,
            duration: 0.6,
            stagger: 0.12,
            ease: "back.out(1.4)",
            scrollTrigger: { trigger: stepsRef.current, start: "top 80%" },
          },
        );
      }
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-20 md:py-32 px-6"
      style={{ background: "linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%)" }}
    >
      <div ref={headingRef} className="text-center mb-20 opacity-0">
        <p className="text-brand-mint font-semibold tracking-widest uppercase text-sm mb-3">
          How It Works
        </p>
        <h2 className="text-3xl md:text-5xl font-bold tracking-tight max-w-2xl mx-auto">
          Deliciously Healthy Meals, Delivered to You
        </h2>
      </div>
      <div
        ref={stepsRef}
        className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-8 max-w-6xl mx-auto"
      >
        {steps.map((step) => (
          <div
            key={step.step}
            className="relative bg-white rounded-3xl p-8 flex flex-col items-center text-center shadow-lg opacity-0"
          >
            <span className="absolute -top-4 -left-2 text-6xl font-black text-brand-mint/70 select-none">
              {step.step}
            </span>
            <NextImage src={step.img} alt={step.title} className="w-20 h-20 object-contain mb-6" />
            <h3 className="text-lg font-bold mb-2">{step.title}</h3>
            <p className="text-gray-600 text-sm leading-relaxed">{step.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
};
